import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { FbGroup, FbGroupPost, FbExtensionSettings, FbPage, FbPageStory, FbPostLog, FbPostStats, FbProfile } from '@/types'

// ─── Profiles ─────────────────────────────────────────────

export function useFbProfiles() {
  return useQuery<{ profiles: FbProfile[] }>({
    queryKey: ['fb-extension', 'profiles'],
    queryFn: () => api.get('/fb-extension/profiles'),
    refetchInterval: 30000,
  })
}

export function useCreateFbProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string }) =>
      api.post<{ ok: boolean; profile: FbProfile }>('/fb-extension/profiles', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'profiles'] }),
  })
}

export function useUpdateFbProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; default_page_id?: string | null }) =>
      api.put(`/fb-extension/profiles/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'profiles'] }),
  })
}

export function useDeleteFbProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/fb-extension/profiles/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fb-extension', 'profiles'] })
      qc.invalidateQueries({ queryKey: ['fb-extension', 'groups'] })
    },
  })
}

// ─── Groups ──────────────────────────────────────────────

export function useFbGroups(collection?: string, profileId?: string) {
  const params = new URLSearchParams()
  if (collection) params.set('collection', collection)
  if (profileId) params.set('profile_id', profileId)
  const query = params.toString() ? `?${params}` : ''
  return useQuery<{
    groups: FbGroup[]
    collections: { name: string; count: number }[]
    total: number
  }>({
    queryKey: ['fb-extension', 'groups', collection, profileId],
    queryFn: () => api.get(`/fb-extension/groups${query}`),
  })
}

export function useCreateFbGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { url: string; name?: string; collection?: string; profile_id?: string }) =>
      api.post<{ ok: boolean; group: FbGroup }>('/fb-extension/groups', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'groups'] }),
  })
}

export function useImportFbGroups() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      groups: { fb_group_id?: string; name?: string; url: string }[]
      collection?: string
      profile_id?: string
    }) => api.post<{ ok: boolean; total: number; added: number; skipped: number }>(
      '/fb-extension/groups/import',
      data,
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'groups'] }),
  })
}

export function useUpdateFbGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; collection?: string; is_active?: boolean; profile_id?: string }) =>
      api.put(`/fb-extension/groups/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'groups'] }),
  })
}

export function useDeleteFbGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/fb-extension/groups/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'groups'] }),
  })
}

// ─── Pages ──────────────────────────────────────────────

export function useFbPages(profileId?: string) {
  const params = new URLSearchParams()
  if (profileId) params.set('profile_id', profileId)
  const query = params.toString() ? `?${params}` : ''
  return useQuery<{ pages: FbPage[] }>({
    queryKey: ['fb-extension', 'pages', profileId],
    queryFn: () => api.get(`/fb-extension/pages${query}`),
  })
}

export function useCreateFbPage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { profile_id: string; fb_page_id: string; name: string; url?: string }) =>
      api.post<{ ok: boolean; page: FbPage }>('/fb-extension/pages', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'pages'] }),
  })
}

export function useUpdateFbPage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; url?: string; is_active?: boolean }) =>
      api.put(`/fb-extension/pages/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'pages'] }),
  })
}

export function useDeleteFbPage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/fb-extension/pages/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fb-extension', 'pages'] })
      qc.invalidateQueries({ queryKey: ['fb-extension', 'profiles'] })
    },
  })
}

export function useRequestScanPages() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (profileId: string) =>
      api.post<{ ok: boolean }>(`/fb-extension/profiles/${profileId}/scan-pages`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fb-extension', 'pages'] })
      qc.invalidateQueries({ queryKey: ['fb-extension', 'profiles'] })
    },
  })
}

// ─── Posts (Queue) ───────────────────────────────────────

export function useFbPosts(status?: string, page = 1, profileId?: string) {
  const params = new URLSearchParams({ page: String(page) })
  if (status) params.set('status', status)
  if (profileId) params.set('profile_id', profileId)
  return useQuery<{
    data: FbGroupPost[]
    current_page: number
    last_page: number
    total: number
  }>({
    queryKey: ['fb-extension', 'posts', status, page, profileId],
    queryFn: () => api.get(`/fb-extension/posts?${params}`),
    refetchInterval: 5000,
  })
}

export function useCreateFbPost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      message: string
      group_ids: string[]
      image_url?: string
      scheduled_at?: string
      page_id?: string
      share_url?: string
      mention_everyone?: boolean
      comment_text?: string
    }) => api.post<{ ok: boolean; batch_id: string; count: number }>('/fb-extension/posts', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'posts'] }),
  })
}

// ─── Share: Accounts & Page Posts ────────────────────────

export interface ShareAccount {
  id: string
  name: string
  pageId: string
}

export function useShareAccounts() {
  return useQuery<{ accounts: ShareAccount[] }>({
    queryKey: ['fb-extension', 'share-accounts'],
    queryFn: () => api.get('/fb-extension/share-accounts'),
  })
}

export interface FbPagePost {
  id: string
  message: string
  created_time: string | null
  full_picture: string | null
  permalink_url: string | null
}

export function usePagePosts(accountId: string | null) {
  return useQuery<{ posts: FbPagePost[] }>({
    queryKey: ['fb-extension', 'page-posts', accountId],
    queryFn: () => api.get(`/fb-extension/page-posts?account_id=${accountId}`),
    enabled: !!accountId,
  })
}

export function useDeleteFbPost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/fb-extension/posts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'posts'] }),
  })
}

export function useCancelBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (batchId: string) =>
      api.post<{ ok: boolean; cancelled: number }>(`/fb-extension/posts/cancel-batch/${batchId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'posts'] }),
  })
}

export function useClearDonePosts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete('/fb-extension/posts/clear/done'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'posts'] }),
  })
}

export function useClearFailedPosts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete('/fb-extension/posts/clear/failed'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'posts'] }),
  })
}

// ─── Logs ────────────────────────────────────────────────

export function useFbLogs(page = 1, status?: string, profileId?: string) {
  const params = new URLSearchParams({ page: String(page) })
  if (status) params.set('status', status)
  if (profileId) params.set('profile_id', profileId)
  return useQuery<{
    data: FbPostLog[]
    current_page: number
    last_page: number
    total: number
  }>({
    queryKey: ['fb-extension', 'logs', page, status, profileId],
    queryFn: () => api.get(`/fb-extension/logs?${params}`),
  })
}

export function useFbLogStats() {
  return useQuery<FbPostStats>({
    queryKey: ['fb-extension', 'logs', 'stats'],
    queryFn: () => api.get('/fb-extension/logs/stats'),
  })
}

export function useClearFbLogs() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete('/fb-extension/logs'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fb-extension', 'logs'] })
    },
  })
}

// ─── Stories ────────────────────────────────────────────

export function useFbStories(status?: string, page = 1) {
  const params = new URLSearchParams({ page: String(page) })
  if (status) params.set('status', status)
  return useQuery<{
    data: FbPageStory[]
    current_page: number
    last_page: number
    total: number
  }>({
    queryKey: ['fb-extension', 'stories', status, page],
    queryFn: () => api.get(`/fb-extension/stories?${params}`),
    refetchInterval: 5000,
  })
}

export function useCreateFbStory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      account_ids: string[]
      media_url: string
      media_type: 'image' | 'video'
      link_url?: string
      overlay_text?: string
      text_position?: 'middle' | 'bottom'
      scheduled_at?: string
    }) => api.post<{ ok: boolean; batch_id: string; count: number }>('/fb-extension/stories', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'stories'] }),
  })
}

export function useDeleteFbStory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/fb-extension/stories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'stories'] }),
  })
}

export function useClearDoneStories() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete('/fb-extension/stories/clear/done'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'stories'] }),
  })
}

export function useClearFailedStories() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete('/fb-extension/stories/clear/failed'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'stories'] }),
  })
}

// ─── Invites (Page Likers) ───────────────────────────────

export function useFbInvites(status?: string, page = 1) {
  const params = new URLSearchParams({ page: String(page) })
  if (status) params.set('status', status)
  return useQuery<{
    data: import('@/types').FbPageInvite[]
    current_page: number
    last_page: number
    total: number
  }>({
    queryKey: ['fb-extension', 'invites', status, page],
    queryFn: () => api.get(`/fb-extension/invites?${params}`),
    refetchInterval: 5000,
  })
}

export function useCreateFbInvites() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      account_id: string
      posts: { post_id: string; permalink_url?: string; message?: string; reaction_count?: number }[]
      scheduled_at?: string
    }) => api.post<{ ok: boolean; batch_id: string; count: number }>('/fb-extension/invites', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'invites'] }),
  })
}

export function useCreateGroupInvites() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      account_id: string
      urls: string[]
      scheduled_at?: string
    }) => api.post<{ ok: boolean; batch_id: string; count: number }>('/fb-extension/invites/group', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'invites'] }),
  })
}

export function useDeleteFbInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/fb-extension/invites/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'invites'] }),
  })
}

export function useClearDoneInvites() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete('/fb-extension/invites/clear/done'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'invites'] }),
  })
}

export function useClearFailedInvites() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete('/fb-extension/invites/clear/failed'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'invites'] }),
  })
}

// ─── Page–Group Links ───────────────────────────────────

// Get group IDs linked to a specific page
export function usePageGroupIds(pageId: string | null) {
  return useQuery({
    queryKey: ['fb-page-groups', pageId],
    queryFn: () => api.get<string[]>(`/fb-extension/pages/${pageId}/groups`),
    enabled: !!pageId,
  })
}

// Sync groups for a page (set which groups are linked)
export function useSyncPageGroups() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ pageId, groupIds }: { pageId: string; groupIds: string[] }) =>
      api.post(`/fb-extension/pages/${pageId}/groups`, { group_ids: groupIds }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['fb-page-groups', vars.pageId] })
      qc.invalidateQueries({ queryKey: ['fb-extension', 'pages'] })
      qc.invalidateQueries({ queryKey: ['fb-extension', 'groups'] })
      qc.invalidateQueries({ queryKey: ['fb-page-group-map'] })
    },
  })
}

// ─── Settings ────────────────────────────────────────────

export function useFbSettings() {
  return useQuery<{ settings: FbExtensionSettings }>({
    queryKey: ['fb-extension', 'settings'],
    queryFn: () => api.get('/fb-extension/settings'),
  })
}

export function useUpdateFbSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.put('/fb-extension/settings', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fb-extension', 'settings'] }),
  })
}

// ─── Token ───────────────────────────────────────────────

export function useGenerateExtToken() {
  const qc = useQueryClient()
  return useMutation<{ ok: boolean; token: string; setupUrl: string }, Error, { profile_id?: string } | void>({
    mutationFn: (data) => api.post('/fb-extension/generate-token', data || {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fb-extension', 'settings'] })
      qc.invalidateQueries({ queryKey: ['fb-extension', 'profiles'] })
    },
  })
}

// ─── Page-Group Map (batch) ─────────────────────────────────

export function usePageGroupMap(profileId: string | null) {
  return useQuery<{ map: Record<string, string[]> }>({
    queryKey: ['fb-page-group-map', profileId],
    queryFn: () => api.get(`/fb-extension/page-group-map?profile_id=${profileId}`),
    enabled: !!profileId,
  })
}

export function useImportStructured() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      profile_id: string
      categories: Array<{ name: string; page_id: string | null; urls: string[] }>
    }) => api.post<{
      ok: boolean
      created: number
      existing: number
      duplicates_skipped: number
      links_created: number
      categories_processed: number
    }>('/fb-extension/groups/import-structured', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fb-extension', 'groups'] })
      qc.invalidateQueries({ queryKey: ['fb-extension', 'pages'] })
      qc.invalidateQueries({ queryKey: ['fb-page-group-map'] })
    },
  })
}

// ─── Map Accounts (Mapa tab) ────────────────────────────────

export interface MapAccount {
  accountId: string
  name: string
  pageId: string
  fbPageId: string | null
  groupIds: string[]
}

export function useMapAccounts(profileId: string | null) {
  return useQuery<{ accounts: MapAccount[] }>({
    queryKey: ['fb-map-accounts', profileId],
    queryFn: () => api.get(`/fb-extension/map-accounts?profile_id=${profileId}`),
    enabled: !!profileId,
  })
}

export function useMapSyncGroups() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { page_id: string; profile_id: string; group_ids: string[] }) =>
      api.post<{ ok: boolean; fbPageId: string; count: number }>('/fb-extension/map-sync-groups', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fb-map-accounts'] })
      qc.invalidateQueries({ queryKey: ['fb-extension', 'groups'] })
      qc.invalidateQueries({ queryKey: ['fb-page-group-map'] })
    },
  })
}

export function useRevokeExtToken() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data?: { profile_id?: string }) => api.delete('/fb-extension/revoke-token', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fb-extension', 'settings'] })
      qc.invalidateQueries({ queryKey: ['fb-extension', 'profiles'] })
    },
  })
}

// ─── Unroutable Jobs ─────────────────────────────────────

export function useUnroutableJobs() {
  return useQuery<{
    unroutable_posts: number
    unroutable_invites: number
    total: number
    online_profiles: number
  }>({
    queryKey: ['extension', 'unroutable-jobs'],
    queryFn: () => api.get('/fb-extension/unroutable-jobs'),
    refetchInterval: 30000,
  })
}
